import { useEffect, useMemo, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardBody,
  Input,
  LoadingSkeleton,
} from '@nexora/design-system';
import { useBankAccounts } from './useBankAccounts';
import { usePayables, type Payable } from './usePayables';
import { useWriteOffPayable, type WriteOffPayableResult } from './useTreasuryDashboards';

/* ── Contratos reais (backend/.review-loop/reports/served-routes.json) ──
 * GET  /finance/payables/:id            → Payable (detalhe do título)
 * POST /finance/payables/:id/write-off  → WriteOffPayableResultDto
 *   corpo: WriteOffPayableDto { principal, interest?, fine?, discount?,
 *          paymentDate, paymentMethod, accountId, notes? }
 * (DTOs em backend/.../dto/treasury.dto.ts)
 */

const PAYMENT_METHODS = ['PIX', 'TED', 'BOLETO', 'DINHEIRO', 'CARTAO'];

function statusBadge(status: Payable['status']) {
  const map: Record<string, { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }> = {
    OPEN: { variant: 'info', label: 'Em aberto' },
    PARTIALLY_SETTLED: { variant: 'warning', label: 'Parcial' },
    SETTLED: { variant: 'success', label: 'Liquidado' },
    OVERDUE: { variant: 'error', label: 'Vencido' },
    RENEGOTIATED: { variant: 'warning', label: 'Renegociado' },
    CANCELLED: { variant: 'neutral', label: 'Cancelado' },
  };
  return map[status] ?? { variant: 'neutral' as const, label: status };
}

export function PayableWriteOffPage(): JSX.Element {
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  const routeId = params['id'] ?? params['titleId'] ?? params['payableId'];

  const { data: listData } = usePayables({ page: 1, limit: 50 });
  const openPayables = useMemo(
    () => (listData?.data ?? []).filter((p) => p.status === 'OPEN' || p.status === 'PARTIALLY_SETTLED' || p.status === 'OVERDUE'),
    [listData],
  );

  const [selectedId, setSelectedId] = useState<string>(routeId ?? '');
  useEffect(() => {
    if (routeId) setSelectedId(routeId);
  }, [routeId]);

  const payableQ = useQuery<Payable>({
    queryKey: ['finance', 'payables', selectedId],
    queryFn: () =>
      apiClient.get<Payable>(`/finance/payables/${selectedId}`, {
        params: { page: '1', pageSize: '1' },
      }),
    enabled: !!selectedId,
  });
  const payable = payableQ.data;

  return (
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="font-label-sm text-label-sm text-on-surface-variant mb-1 uppercase tracking-wider">Payables / Processing</p>
          <h1 className="font-display-lg text-display-lg text-primary m-0">Efetuar Pagamento</h1>
        </div>
        {payable && (
          <Badge variant={statusBadge(payable.status).variant} size="md" dot>
            {statusBadge(payable.status).label}
          </Badge>
        )}
      </div>

      {/* Seletor de título: roda sem rota parametrizada; com :id, preseleciona */}
      <Card variant="outlined" padding="md" className="mb-lg">
        <CardHeader>
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Título a baixar</h3>
        </CardHeader>
        <CardBody>
          {openPayables.length === 0 ? (
            <div style={{ padding: '1rem 0', color: '#74777f' }}>
              Nenhuma conta a pagar em aberto. Baixas exigem título com saldo.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-sm">
              {openPayables.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedId(p.id)}
                  className={`text-left p-sm rounded border transition-colors cursor-pointer ${
                    selectedId === p.id
                      ? 'border-primary bg-primary/5'
                      : 'border-outline-variant bg-surface-container-lowest hover:bg-surface-container-low'
                  }`}
                >
                  <div className="font-body-sm text-body-sm font-medium text-on-surface">{p.supplierName}</div>
                  <div className="font-label-sm text-label-sm text-on-surface-variant">{p.documentNumber}</div>
                  <div className="font-label-md text-label-md text-on-surface tabular-nums mt-xs">
                    {formatCurrency(p.openBalance)}
                  </div>
                  <div className="font-label-sm text-label-sm text-on-surface-variant">Vence {formatDate(p.dueDate)}</div>
                </button>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {payableQ.isLoading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
          <LoadingSkeleton />
          <span style={{ fontSize: '0.875rem' }}>Carregando título...</span>
        </div>
      )}

      {payableQ.error && !payableQ.isLoading && (
        <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
          <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar título</p>
          <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>{(payableQ.error as Error).message}</p>
        </div>
      )}

      {!payableQ.isLoading && !payableQ.error && payable && (
        <WriteOffForm key={payable.id} payable={payable} />
      )}
    </div>
  );
}

function WriteOffForm({ payable }: { payable: Payable }): JSX.Element {
  const { data: accounts } = useBankAccounts();
  const writeOff = useWriteOffPayable();

  const [principal, setPrincipal] = useState(payable.openBalance.toString());
  const [interest, setInterest] = useState(payable.interestAmount ? payable.interestAmount.toString() : '');
  const [fine, setFine] = useState(payable.fineAmount ? payable.fineAmount.toString() : '');
  const [discount, setDiscount] = useState(payable.discountAmount ? payable.discountAmount.toString() : '');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0] ?? '');
  const [paymentMethod, setPaymentMethod] = useState('PIX');
  const [accountId, setAccountId] = useState('');
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState<WriteOffPayableResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId && accounts && accounts.length > 0) setAccountId(accounts[0]?.id ?? '');
  }, [accounts, accountId]);

  const num = (s: string) => (s === '' ? 0 : parseFloat(s) || 0);
  const principalV = num(principal);
  const interestV = num(interest);
  const fineV = num(fine);
  const discountV = num(discount);
  const total = principalV + interestV + fineV - discountV;
  const valid = principalV > 0 && !!paymentDate && !!paymentMethod && !!accountId;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!valid) return;
    setError(null);
    try {
      const res = await writeOff.mutateAsync({
        id: payable.id,
        principal: principalV,
        ...(interestV > 0 ? { interest: interestV } : {}),
        ...(fineV > 0 ? { fine: fineV } : {}),
        ...(discountV > 0 ? { discount: discountV } : {}),
        paymentDate,
        paymentMethod,
        accountId,
        ...(notes ? { notes } : {}),
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao dar baixa.');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-lg">
      {/* Dados do título */}
      <Card variant="outlined" padding="md">
        <CardHeader>
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Dados do Título</h3>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 gap-md mb-md">
            <div>
              <p className="font-label-sm text-label-sm text-on-surface-variant mb-1">Fornecedor</p>
              <p className="font-body-md text-body-md text-on-surface m-0 font-medium">{payable.supplierName}</p>
            </div>
            <div>
              <p className="font-label-sm text-label-sm text-on-surface-variant mb-1">Documento</p>
              <p className="font-body-md text-body-md text-on-surface m-0">{payable.documentNumber}</p>
            </div>
            <div>
              <p className="font-label-sm text-label-sm text-on-surface-variant mb-1">Vencimento</p>
              <p className="font-body-md text-body-md text-on-surface m-0">{formatDate(payable.dueDate)}</p>
            </div>
            <div>
              <p className="font-label-sm text-label-sm text-on-surface-variant mb-1">Categoria</p>
              <p className="font-body-md text-body-md text-on-surface m-0">{payable.category}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-md bg-surface-container-low p-md rounded-lg">
            <div>
              <p className="font-label-sm text-label-sm text-on-surface-variant mb-1">Valor original</p>
              <p className="font-body-md text-body-md text-on-surface m-0 tabular-nums">{formatCurrency(payable.originalAmount)}</p>
            </div>
            <div>
              <p className="font-label-sm text-label-sm text-on-surface-variant mb-1">Saldo em aberto</p>
              <p className="font-body-md text-body-md text-on-surface m-0 font-bold tabular-nums">{formatCurrency(payable.openBalance)}</p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Composição do valor */}
      <Card variant="outlined" padding="md">
        <CardHeader>
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Composição do Valor</h3>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit} className="fin-form">
            <Input
              label="Principal (R$)"
              type="number"
              step="0.01"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
              required
            />
            <div className="fin-form-row">
              <Input label="Juros (+) R$" type="number" step="0.01" value={interest} onChange={(e) => setInterest(e.target.value)} />
              <Input label="Multa (+) R$" type="number" step="0.01" value={fine} onChange={(e) => setFine(e.target.value)} />
            </div>
            <div className="fin-form-row">
              <Input label="Desconto (−) R$" type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} />
              <Input label="Data do pagamento" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} required />
            </div>

            {/* Origem e forma de pagamento */}
            <div className="fin-field">
              <label htmlFor="wo-account" className="fin-label">Conta de origem</label>
              <select
                id="wo-account"
                className="fin-select"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
              >
                {(accounts ?? []).length === 0 && <option value="">Nenhuma conta cadastrada</option>}
                {(accounts ?? []).map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name} {acc.bankName ? `· ${acc.bankName}` : ''} (saldo {formatCurrency(acc.currentBalance)})
                  </option>
                ))}
              </select>
            </div>
            <div className="fin-field">
              <label htmlFor="wo-method" className="fin-label">Forma de pagamento</label>
              <select
                id="wo-method"
                className="fin-select"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <Input label="Observações" value={notes} onChange={(e) => setNotes(e.target.value)} />

            <div className="flex justify-between items-center bg-primary-container text-on-primary-container p-md rounded-lg">
              <span className="font-body-lg text-body-lg font-medium">Total a pagar</span>
              <span className="font-headline-md text-headline-md font-bold tabular-nums">{formatCurrency(total)}</span>
            </div>

            {error && <p style={{ color: '#ba1a1a', margin: 0, fontSize: '0.875rem' }}>{error}</p>}

            <div className="fin-form-actions">
              <Button variant="primary" type="submit" loading={writeOff.isPending} disabled={!valid}>
                Efetuar baixa
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {/* Prévia contábil — aparece após a baixa */}
      {result && (
        <Card variant="outlined" padding="0" className="lg:col-span-2">
          <CardHeader className="px-md pt-md">
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Prévia do Lançamento Contábil</h3>
            <Badge variant="success" size="sm" dot>Baixa registrada · Transação {result.transactionId.slice(0, 8)}</Badge>
          </CardHeader>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Tipo', 'Conta Contábil', 'Descrição', 'Valor'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: h === 'Valor' ? 'right' : 'left',
                        padding: '0.75rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.accountingPreview.entries.map((e, i) => (
                  <tr key={`${e.accountCode}-${i}`}>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      <Badge variant={e.type === 'DEBIT' ? 'info' : 'success'} size="sm">{e.type}</Badge>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      <span className="fin-mono">{e.accountCode}</span>
                      <span style={{ display: 'block', color: '#43474e' }}>{e.accountName}</span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', color: '#43474e' }}>{e.description}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', textAlign: 'right', fontWeight: 600, fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(e.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center px-md py-sm border-t border-outline-variant">
            <span className="font-body-sm text-body-sm text-on-surface-variant">
              Débitos {formatCurrency(result.accountingPreview.totalDebit)} · Créditos {formatCurrency(result.accountingPreview.totalCredit)}
              {result.accountingPreview.balanced ? ' · Partidas dobradas ✓' : ' · ⚠ Desbalanceado'}
            </span>
            <span className="font-label-md text-label-md text-on-surface">
              Novo saldo: {formatCurrency(result.newBalance)} · {result.newStatus}
            </span>
          </div>
        </Card>
      )}

      <style>{`
        .fin-form { display: flex; flex-direction: column; gap: 1rem; }
        .fin-form-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; }
        .fin-form-actions { display: flex; gap: 0.75rem; justify-content: flex-end; }
        .fin-field { display: flex; flex-direction: column; gap: 0.25rem; }
        .fin-label { margin: 0; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; color: #43474e; }
        .fin-select { padding: 0.5rem; background: #f7f9fb; border: 1px solid #c4c6cf; border-radius: 2px; font: inherit; font-size: 0.875rem; color: #191c1e; }
        .fin-select:focus { outline: none; border-color: #002045; box-shadow: 0 0 0 1px #002045; }
        .fin-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; }
      `}</style>
    </div>
  );
}